// @flow

import _ from 'lodash';
import moment from 'moment';

import ApiObject from '../ApiObject';

// -- utils
import DateUtils from '../../utils/DateUtils';
import ArrayUtils from '../../utils/ArrayUtils';
import BookingUtils from '../../utils/BookingUtils';

// -- constants
import ApiConstants from '../../constants/ApiConstants';

// -- entities
import Moment from '../../utils/Types';
import BookingNotificationSettings from './BookingNotificationSettings';
import BookingSettingsPeriod from './BookingSettingsPeriod';
import BookingSettingsRoom from './BookingSettingsRoom';
import BookingSettingsSpecific from './BookingSettingsSpecific';
import BookingSettingsSalesContact from './BookingSettingsSalesContact';

export default class BookingSettings extends ApiObject {

  /**
   * Number of guests to automatically validate the new booking.
   */
  maxNumberOfGuestsForAutomaticValidation: number = 0;

  /**
	 * Define the maximum number of guests allowed to book. If the user wants more guests, he will
	 * have to contact the group.
	 * The value 0 means that there is no limitation.
	 * Have to be superior to the maxNumberOfGuestsForAutomaticValidation value (or 0)
	 * We throw an error if a booking guest number is > to this value. We don't throw the error if
	 * the booking is made via the restaurant BO.
	 */
  maxNumberOfGuestsForBooking: number = 100;

  /**
   * Maximum time ahead to create a new booking.
   */
  maxTimeAhead: number = 0;

  timeBeforeNoShow: number = 0;

  /**
   * Maximum time ahead to create a new booking. Read-only value from the API.
   */
  maxTimeAheadFromNow: number = 0;

  /**
   * Minimum time ahead to create a new booking.
   */
  minTimeAhead: number = 0;

  /**
   * Time after the beginning of the booking to consider a booking as no show if the booking is
   * still set to NOT_ARRIVED.
   * Timestamp in ms.
   * This feature is disabled.
   */
  //		private Date timeBeforeNoShow                        = 0;

  periods: Array<BookingSettingsPeriod> = [];

  rooms: Array<BookingSettingsRoom> = [];

  notificationSettings: BookingNotificationSettings = new BookingNotificationSettings();

  /**
   * Contains the maximum capacity of the restaurant. We use it to verify that the custom
   * capacities set on the settings are not too high. They must not be higher than this value.
   */
  maximumCapacity: number = 0;

  /**
   * The interval between each slots of reservation.
   * Can be 15 or 30 minutes.
   */
  slotInterval: number = 0;

  salesContact: BookingSettingsSalesContact = new BookingSettingsSalesContact();

  /**
   * The time (mostly in hours) where the reminder have to be send, before the booking date.
   */
  reminderTimeGap: number;

  /**
   * The minimum time between now and the booking date to create a reminder.
   */
  timeForReminderBeforeBooking: number;

  // --

  postJsonAssignation(data: Object) {
    this.periods = [];
    _.each(data.periods, (periodData: Object) => {
      const period:BookingSettingsPeriod = new BookingSettingsPeriod();
      period.fromJson(periodData);

      this.periods.push(period);
    });

    this.rooms = [];
    _.each(data.rooms, (roomData: Object) => {
      const room:BookingSettingsRoom = new BookingSettingsRoom();
      room.fromJson(roomData);

      this.rooms.push(room);
    });

    this.notificationSettings = new BookingNotificationSettings();
    this.notificationSettings.fromJson(data.notificationSettings)
  }

  // -- utils
  isAhead(): boolean {
    // TODO.
  }

  getReminderTimeGapDate(): Moment {
    return DateUtils.fromMsTimestamp(this.reminderTimeGap);
  }

  getTimeForReminderBeforeBookingDate(): Moment {
    return DateUtils.fromMsTimestamp(this.timeForReminderBeforeBooking);
  }

  // -- format

  getFormattedMaxTimeAhead(): string {
    return `${this.maxTimeAhead / 60 / 60 / 24 / 1000} jours`;
  }

  // -- rooms

  findRoomByName(name: string): ?BookingSettingsRoom {
    const found = _.find(this.rooms, (room: BookingSettingsRoom) => {
      return room.name === name;
    });

    if (_.isUndefined(found)) {
      return null;
    }

    return found;
  }

  addRoom(roomToAdd: BookingSettingsRoom) {
    this.rooms.push(roomToAdd);
  }

  updateRoom(roomToUpdate: BookingSettingsRoom) {
    ArrayUtils.updateObjectOrAdd(this.rooms, roomToUpdate, (room: BookingSettingsRoom) => {
      return room.id === roomToUpdate.id;
    });
  }

  deleteRoom(roomToDelete: BookingSettingsRoom) {
    this.rooms = _.filter(this.rooms, (room: BookingSettingsRoom) => {
      return room.id !== roomToDelete.id;
    });

    this.specifics = _.filter(this.specifics, (specific: BookingSettingsSpecific) => {
      return specific.roomId !== roomToDelete.id;
    });

    // -- TODO: remove on periods -> each day -> roomsSettings
  }

  // -- periods
  getPeriodSettings(periodId: number) {
    const found = _.find(this.periods, (period: BookingSettingsPeriod) => {
      return period.period === periodId;
    });

    return _.isUndefined(found) ? null : found;
  }

  addPeriodSettings(periodToAdd: BookingSettingsPeriod) {
    this.setPeriodSettings(periodToAdd);
  }

  setPeriodSettings(periodToSet: BookingSettingsPeriod) {
    const tmp = [];

    _.each(this.periods, (period: BookingSettingsPeriod) => {
      if (period.period !== periodToSet.period) {
        tmp.push(period);
      }
    });

    tmp.push(periodToSet);

    // -- sort periods. We use the period enum number to sort (0: morning -> to night)
    tmp.sort((a: BookingSettingsPeriod, b: BookingSettingsPeriod) => {
      return a.period - b.period;
    });

    this.periods = tmp;
  }

  deletePeriodSettings(periodSettingsToDelete: BookingSettingsPeriod) {
    this.periods = _.filter(this.periods, (periodSettings: BookingSettingsPeriod) => {
      return periodSettings.period !== periodSettingsToDelete.period;
    });

    this.specifics = _.filter(this.specifics, (specific: BookingSettingsSpecific) => {
      return specific.period !== periodSettingsToDelete.period;
    });
  }

  // -- slots

  getSlotsAvailableForDayOnPeriod(day: number, period: number) {
    const daySettings = this.getPeriodSettings(period).getSettingsForDay(day);

    if (_.isNull(daySettings)) {
      return [];
    }

    return BookingUtils.generateSlotsForInterval(
      daySettings.openingHour,
      daySettings.closingHour,
      this.slotInterval
    );
  }

  getDayWithSlotsOnPeriod(period: number) {
    return this.getPeriodSettings(period).getDayWithSlots();
  }

  // -- is valid ?
  isValid(): boolean {
    if (this.maxTimeAhead === 0) {
      console.log('BookingSettings is invalid (maxTime or minTime)');
      return false;
    }

    if (_.isEmpty(this.rooms)) {
      return false;
    }

    if (_.isEmpty(this.periods)) {
      return false;
    }

    let res = true;
    _.each(this.periods, (period: BookingSettingsPeriod) => {
      if (!period.isValid(this)) {
        res = false;
        return false; // quit the loop
      }
    });

    return res;
  }

  getPeriodsSettingsForDate(date): boolean {
    if (typeof date !== 'object') {
      date = DateUtils.fromMsTimestamp(date);
    }

    const periods = [];
    const day = date.weekday();

    _.each(this.periods, (period: BookingSettingsPeriod) => {
      if (period.isEnableForDay(day)) {
        periods.push(period);
      }
    });

    return periods;
  }

  /**
   * This method should be use with BookingSettingsSpecificsList#periodIsEnableForDate.
   * @param  {[type]} period: number [description]
   * @param  {[type]} date          [description]
   * @return {[type]}               [description]
   */
  periodIsEnableForDate(period: number, date): boolean {
    if (typeof date !== 'object') {
      date = DateUtils.fromMsTimestamp(date);
    }

    const periodSettings:BookingSettingsPeriod = this.getPeriodSettings(period);

    if (!periodSettings.isEnableForDay(date.weekday())) {
      return false;
    }

    return true;
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/booking/BookingSettings.js